import { useCallback, useEffect, useRef, useState } from 'react';
import { getSessions } from '../../services/session';
import { readLocalSessions } from '../utils/session-util';
import { toPreview } from '../utils/text-util';

/**
 * Conversation history for the left rail.
 *
 * Two sources are merged:
 *
 *  - `/apps/{appId}/sessions`, the same API the platform's sessions list uses.
 *    It sits behind the Cognito authorizer, so an anonymous guest gets a 401
 *    and this half is simply empty.
 *  - the browser-local index, which records every session this visitor has
 *    taken part in.
 *
 * The API wins on conflicts (its `displayMessage`/`modifiedAt` are
 * authoritative), and the result is sorted most-recently-active first.
 */
function mergeSessions(apiSessions, localSessions) {
  const byId = new Map();

  localSessions.forEach(session => {
    byId.set(session.sessionId, { ...session });
  });

  apiSessions.forEach(session => {
    const existing = byId.get(session.sessionId);
    byId.set(session.sessionId, {
      ...existing,
      ...session,
      displayMessage: toPreview(session.displayMessage || existing?.displayMessage || ''),
      local: false,
    });
  });

  return Array.from(byId.values()).sort((a, b) => {
    const left = new Date(b.modifiedAt || b.createdAt || 0).getTime();
    const right = new Date(a.modifiedAt || a.createdAt || 0).getTime();
    return left - right;
  });
}

export function useSessions({ appId, accessKey, refreshToken }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [unauthorized, setUnauthorized] = useState(false);

  // The listing is fetched asynchronously and can resolve after the component
  // has gone away (a remount, or navigating while the request is in flight).
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    if (!appId) {
      return;
    }

    setLoading(true);
    const local = readLocalSessions(appId);

    // Show what is known locally straight away, then reconcile with the API.
    setSessions(previous => (previous.length === 0 ? local : previous));

    const response = await getSessions(appId, undefined, undefined, accessKey);
    if (!mountedRef.current) {
      return;
    }

    setUnauthorized(Boolean(response.unauthorized));
    setSessions(mergeSessions(response.data || [], local));
    setLoading(false);
  }, [appId, accessKey]);

  useEffect(() => {
    load();
  }, [load, refreshToken]);

  return { sessions, loading, unauthorized, reload: load };
}
